/**
 * GpuSystemsField — the WebGL2 ping-pong GPGPU engine for the shaders type's
 * systems department. A verbatim port of the PROVEN spike
 * (deploy/public/gpu-spike-tmp.html, docs/PERF-shaders-spike.md): RGBA32F data
 * textures, an MRT fragment-shader update pass (no readback), and a render
 * pass whose vertex shader texelFetches the position texture to place points.
 * That spike sustained 1M particles at 60fps on this GPU, so the sim math here
 * is not new — only its packaging into a class + the screen-space render
 * transform + premultiplied compositing (so a Pixi Sprite can layer it) are.
 *
 * REPRESENTATIVE SYSTEMS WORKLOAD (the spike's honest port, not a toy sine):
 * mirrors particlesOptimised systems lines — physics.ts physicsLerp
 * (easeOutCubic for half, linear for half) lerping a rel position start->grid
 * target over a duration, retargeting to a new grid cell on arrival, plus
 * integrate.ts eased drift of the render position toward the sim target
 * (value += (target-value)/ease * dt). pow() exercises the transcendental unit
 * exactly like Math.pow in physicsLerp (the GPU-parity caveat, design §6).
 *
 * State packing (2 RGBA32F texels per particle, ping-ponged):
 *   s1 = (rx, ry, t, dur)   rx/ry = render position in [-1,1] sim space
 *   s2 = (sx, sy, ex, ey)   lerp start / lerp end (grid target)
 *
 * The field owns its own textures sized by `count` — decoupled from the shared
 * ParticleCore partition (design §8.6, the variable-count buffer). All GL state
 * this touches is saved/restored by the caller's GLStateGuard.
 */

export interface FieldRenderView {
	/** Framebuffer to draw the points into (the Pixi RenderTexture's FBO). */
	framebuffer: WebGLFramebuffer;
	/** Device-pixel size of that framebuffer (viewport). */
	pixelWidth: number;
	pixelHeight: number;
	/** Systems field centre on screen, in LOGICAL px (moves with the drag). */
	centreX: number;
	centreY: number;
	/** Field half-extents in logical px. */
	halfWidth: number;
	halfHeight: number;
	/** Logical viewport (innerWidth/innerHeight) for the clip-space mapping. */
	viewportWidth: number;
	viewportHeight: number;
	/** Renderer resolution (device px per logical px) — scales the point size. */
	resolution: number;
}

const GRID_CELLS = 42.0;
const DRIFT_EASE_MIN = 1.0;

const UPDATE_GLSL = `
float hash(float p){ return fract(sin(p*12.9898)*43758.5453123); }
void simStep(int idx, float dt, float tt, inout vec4 s1, inout vec4 s2){
  float rx=s1.x, ry=s1.y, t=s1.z, dur=s1.w;
  float sx=s2.x, sy=s2.y, ex=s2.z, ey=s2.w;
  t += dt / dur;
  if (t >= 1.0){
    sx = ex; sy = ey;
    float fi = float(idx);
    float h1 = hash(fi + tt*0.7);
    float h2 = hash(fi*1.7 + tt*1.31);
    float g = ${GRID_CELLS.toFixed(1)};
    ex = (floor(h1*g)/g)*1.9 - 0.95;
    ey = (floor(h2*g)/g)*1.9 - 0.95;
    dur = 40.0 + hash(fi + tt*0.37)*45.0;
    t = 0.0;
  }
  float a = t;
  if ((idx & 1) == 1) a = 1.0 - pow(1.0 - t, 3.0);
  float simX = mix(sx, ex, a);
  float simY = mix(sy, ey, a);
  float ease = ${DRIFT_EASE_MIN.toFixed(1)} + float(idx & 3);
  rx += (simX - rx) / ease * dt;
  ry += (simY - ry) / ease * dt;
  s1 = vec4(rx, ry, t, dur);
  s2 = vec4(sx, sy, ex, ey);
}`;

function jsHash(p: number): number {
	const x = Math.sin(p * 12.9898) * 43758.5453123;
	return x - Math.floor(x);
}

export class GpuSystemsField {
	private _gl: WebGL2RenderingContext;
	private _count: number;
	private _colour: [number, number, number];
	private _pointSizePx: number;

	private _w = 0;
	private _h = 0;
	private _tex: Array<WebGLTexture | null> = [null, null, null, null]; //[readS1, readS2, writeS1, writeS2]
	private _fbo: WebGLFramebuffer | null = null;

	private _updateProgram: WebGLProgram | null = null;
	private _renderProgram: WebGLProgram | null = null;
	private _quadVao: WebGLVertexArrayObject | null = null;
	private _pointVao: WebGLVertexArrayObject | null = null;

	private _u: Record<string, WebGLUniformLocation | null> = {};
	private _r: Record<string, WebGLUniformLocation | null> = {};

	constructor(gl: WebGL2RenderingContext, count: number, colour: number, pointSizePx: number) {
		this._gl = gl;
		this._count = count;
		this._pointSizePx = pointSizePx;
		this._colour = [((colour >> 16) & 0xff) / 255, ((colour >> 8) & 0xff) / 255, (colour & 0xff) / 255];
		this._build();
	}

	public get count(): number {
		return this._count;
	}

	/** One ping-pong simulation step (MRT update pass into our own FBO). */
	public update(dt: number, time: number): void {
		const gl = this._gl;
		gl.disable(gl.RASTERIZER_DISCARD); //Pixi leaves this ON — it discards all draws (not clears)
		gl.bindFramebuffer(gl.FRAMEBUFFER, this._fbo);
		gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this._tex[2], 0);
		gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, this._tex[3], 0);
		gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
		gl.viewport(0, 0, this._w, this._h);
		gl.disable(gl.BLEND);
		gl.disable(gl.SCISSOR_TEST);
		gl.useProgram(this._updateProgram);
		gl.activeTexture(gl.TEXTURE0);
		gl.bindTexture(gl.TEXTURE_2D, this._tex[0]);
		gl.uniform1i(this._u.S1, 0);
		gl.activeTexture(gl.TEXTURE1);
		gl.bindTexture(gl.TEXTURE_2D, this._tex[1]);
		gl.uniform1i(this._u.S2, 1);
		gl.uniform1f(this._u.Dt, dt);
		gl.uniform1f(this._u.Time, time);
		gl.uniform2i(this._u.Dim, this._w, this._h);
		gl.uniform1i(this._u.N, this._count);
		gl.bindVertexArray(this._quadVao);
		gl.drawArrays(gl.TRIANGLES, 0, 3);
		gl.bindVertexArray(null);
		//detach so the swapped textures are never both attachment + sampler:
		gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, null, 0);
		//swap read<->write
		this._tex = [this._tex[2], this._tex[3], this._tex[0], this._tex[1]];
	}

	/** Draw the field's points into `view.framebuffer`, mapped into the systems
	 *  screen region, premultiplied over a transparent target. */
	public render(view: FieldRenderView): void {
		const gl = this._gl;
		gl.bindFramebuffer(gl.FRAMEBUFFER, view.framebuffer);
		gl.viewport(0, 0, view.pixelWidth, view.pixelHeight);
		gl.disable(gl.RASTERIZER_DISCARD);
		gl.disable(gl.SCISSOR_TEST);
		gl.disable(gl.DEPTH_TEST);
		//Pixi 8 uses the stencil buffer for masks and can leave STENCIL_TEST ON.
		//glClear ignores the stencil test but rasterized DRAWS do not — an
		//enabled stencil test against our fresh RT (stencil all 0) rejects every
		//fragment, so the field drew nothing while the clear still wrote. THIS
		//was the "draw produces no coverage but clear works" bug.
		gl.disable(gl.STENCIL_TEST);
		//Pixi leaves CULL_FACE enabled — with our quad winding every triangle
		//would be culled (clears + our own-FBO passes are unaffected, which is
		//why the field simulated but drew nothing into the RT):
		gl.disable(gl.CULL_FACE);
		//clear the Pixi RenderTexture to transparent each frame (no ghosting):
		gl.clearColor(0, 0, 0, 0);
		gl.clear(gl.COLOR_BUFFER_BIT);
		//premultiplied "over" — matches Pixi's premultiplied RenderTexture:
		gl.enable(gl.BLEND);
		gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

		gl.useProgram(this._renderProgram);
		gl.activeTexture(gl.TEXTURE0);
		gl.bindTexture(gl.TEXTURE_2D, this._tex[0]);
		gl.uniform1i(this._r.S1, 0);
		gl.uniform2i(this._r.Dim, this._w, this._h);
		gl.uniform2f(this._r.Centre, view.centreX, view.centreY);
		gl.uniform2f(this._r.Half, view.halfWidth, view.halfHeight);
		gl.uniform2f(this._r.Viewport, view.viewportWidth, view.viewportHeight);
		gl.uniform1f(this._r.Pt, this._pointSizePx); //logical px (resolution cancels above)
		gl.uniform3f(this._r.Colour, this._colour[0], this._colour[1], this._colour[2]);
		gl.bindVertexArray(this._pointVao);
		gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, this._count);
		gl.bindVertexArray(null);
	}

	public destroy(): void {
		const gl = this._gl;
		this._tex.forEach((t) => t && gl.deleteTexture(t));
		this._tex = [null, null, null, null];
		if (this._fbo) gl.deleteFramebuffer(this._fbo);
		if (this._updateProgram) gl.deleteProgram(this._updateProgram);
		if (this._renderProgram) gl.deleteProgram(this._renderProgram);
		if (this._quadVao) gl.deleteVertexArray(this._quadVao);
		if (this._pointVao) gl.deleteVertexArray(this._pointVao);
	}

	//Build
	//-----------------------------------------------------------------------------------------------
	private _build(): void {
		const gl = this._gl;
		const N = this._count;
		const W = Math.ceil(Math.sqrt(N));
		const H = Math.ceil(N / W);
		this._w = W;
		this._h = H;

		const { s1, s2 } = this._seed(W, H);
		this._tex[0] = this._makeStateTex(W, H, s1);
		this._tex[1] = this._makeStateTex(W, H, s2);
		this._tex[2] = this._makeStateTex(W, H, null);
		this._tex[3] = this._makeStateTex(W, H, null);
		this._fbo = gl.createFramebuffer();

		const updVs = `#version 300 es
		precision highp float; layout(location=0) in vec2 aPos;
		void main(){ gl_Position = vec4(aPos, 0.0, 1.0); }`;
		const updFs = `#version 300 es
		precision highp float; precision highp int;
		layout(location=0) out vec4 o1; layout(location=1) out vec4 o2;
		uniform highp sampler2D uS1, uS2; uniform float uDt, uTime; uniform ivec2 uDim; uniform int uN;
		${UPDATE_GLSL}
		void main(){
			ivec2 tc = ivec2(gl_FragCoord.xy);
			int idx = tc.y*uDim.x + tc.x;
			vec4 s1 = texelFetch(uS1, tc, 0);
			vec4 s2 = texelFetch(uS2, tc, 0);
			if (idx < uN) simStep(idx, uDt, uTime, s1, s2);
			o1 = s1; o2 = s2;
		}`;
		this._updateProgram = this._program(updVs, updFs);
		this._u = {
			S1: gl.getUniformLocation(this._updateProgram, 'uS1'),
			S2: gl.getUniformLocation(this._updateProgram, 'uS2'),
			Dt: gl.getUniformLocation(this._updateProgram, 'uDt'),
			Time: gl.getUniformLocation(this._updateProgram, 'uTime'),
			Dim: gl.getUniformLocation(this._updateProgram, 'uDim'),
			N: gl.getUniformLocation(this._updateProgram, 'uN')
		};

		//INSTANCED QUADS, not GL_POINTS: ANGLE-Metal does not rasterize GL_POINTS
		//to a non-default framebuffer (the systems field renders into a Pixi
		//RenderTexture FBO to composite) — verified exhaustively. Instanced
		//triangles rasterize to FBOs on every backend AND uncap the dot size.
		//One unit quad (aCorner in [-0.5,0.5]) drawn `count` times; the position
		//comes from the state texture via gl_InstanceID, the corner offsets it.
		const rndVs = `#version 300 es
		precision highp float; precision highp int;
		layout(location=0) in vec2 aCorner;                    // unit-quad corner [-0.5,0.5]
		uniform highp sampler2D uS1; uniform ivec2 uDim; uniform float uPt;
		uniform vec2 uCentre, uHalf, uViewport;
		out vec2 vLocal; out float vShade;
		void main(){
			int idx = gl_InstanceID;
			ivec2 tc = ivec2(idx % uDim.x, idx / uDim.x);
			vec4 s1 = texelFetch(uS1, tc, 0);
			vec2 screen = uCentre + s1.xy * uHalf;               // logical px centre
			vec2 clipCentre = vec2(screen.x / uViewport.x * 2.0 - 1.0, 1.0 - screen.y / uViewport.y * 2.0);
			//uPt is LOGICAL px; 1 logical px = 2/viewport in clip (resolution cancels):
			vec2 clipOffset = aCorner * uPt * 2.0 / uViewport;
			gl_Position = vec4(clipCentre + clipOffset, 0.0, 1.0);
			vLocal = aCorner;
			vShade = 0.6 + 0.4*fract(float(idx)*0.013);
		}`;
		const rndFs = `#version 300 es
		precision highp float; in vec2 vLocal; in float vShade; out vec4 frag;
		uniform vec3 uColour;
		void main(){
			float d = length(vLocal);                            // 0 centre .. 0.707 corner
			float a = smoothstep(0.5, 0.12, d) * vShade;         // soft round dot
			frag = vec4(uColour * a, a);                         // premultiplied
		}`;
		this._renderProgram = this._program(rndVs, rndFs);
		this._r = {
			S1: gl.getUniformLocation(this._renderProgram, 'uS1'),
			Dim: gl.getUniformLocation(this._renderProgram, 'uDim'),
			Pt: gl.getUniformLocation(this._renderProgram, 'uPt'),
			Centre: gl.getUniformLocation(this._renderProgram, 'uCentre'),
			Half: gl.getUniformLocation(this._renderProgram, 'uHalf'),
			Viewport: gl.getUniformLocation(this._renderProgram, 'uViewport'),
			Colour: gl.getUniformLocation(this._renderProgram, 'uColour')
		};

		//fullscreen triangle for the update pass:
		const quad = new Float32Array([-1, -1, 3, -1, -1, 3]);
		const qb = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, qb);
		gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW);
		this._quadVao = gl.createVertexArray();
		gl.bindVertexArray(this._quadVao);
		gl.enableVertexAttribArray(0);
		gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
		gl.bindVertexArray(null);
		//unit-quad corner buffer for the instanced render draw (triangle strip):
		const corners = new Float32Array([-0.5, -0.5, 0.5, -0.5, -0.5, 0.5, 0.5, 0.5]);
		const cb = gl.createBuffer();
		gl.bindBuffer(gl.ARRAY_BUFFER, cb);
		gl.bufferData(gl.ARRAY_BUFFER, corners, gl.STATIC_DRAW);
		this._pointVao = gl.createVertexArray();
		gl.bindVertexArray(this._pointVao);
		gl.enableVertexAttribArray(0);
		gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
		gl.bindVertexArray(null);
	}

	private _seed(W: number, H: number): { s1: Float32Array; s2: Float32Array } {
		const s1 = new Float32Array(W * H * 4);
		const s2 = new Float32Array(W * H * 4);
		for (let i = 0; i < W * H; i++) {
			const rx = jsHash(i + 0.1) * 1.9 - 0.95;
			const ry = jsHash(i * 1.7 + 0.2) * 1.9 - 0.95;
			const ex = (Math.floor(jsHash(i + 3.0) * GRID_CELLS) / GRID_CELLS) * 1.9 - 0.95;
			const ey = (Math.floor(jsHash(i * 1.7 + 4.0) * GRID_CELLS) / GRID_CELLS) * 1.9 - 0.95;
			const dur = 40 + jsHash(i + 5.0) * 45;
			s1[i * 4] = rx;
			s1[i * 4 + 1] = ry;
			s1[i * 4 + 2] = jsHash(i + 6.0);
			s1[i * 4 + 3] = dur;
			s2[i * 4] = rx;
			s2[i * 4 + 1] = ry;
			s2[i * 4 + 2] = ex;
			s2[i * 4 + 3] = ey;
		}
		return { s1, s2 };
	}

	private _makeStateTex(w: number, h: number, data: Float32Array | null): WebGLTexture {
		const gl = this._gl;
		const t = gl.createTexture();
		gl.bindTexture(gl.TEXTURE_2D, t);
		gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA32F, w, h, 0, gl.RGBA, gl.FLOAT, data);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
		gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
		return t;
	}

	private _compile(type: number, src: string): WebGLShader {
		const gl = this._gl;
		const s = gl.createShader(type);
		gl.shaderSource(s, src);
		gl.compileShader(s);
		if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
			const log = gl.getShaderInfoLog(s);
			gl.deleteShader(s);
			throw new Error('GpuSystemsField shader compile failed: ' + log);
		}
		return s;
	}

	private _program(vs: string, fs: string): WebGLProgram {
		const gl = this._gl;
		const p = gl.createProgram();
		const v = this._compile(gl.VERTEX_SHADER, vs);
		const f = this._compile(gl.FRAGMENT_SHADER, fs);
		gl.attachShader(p, v);
		gl.attachShader(p, f);
		gl.linkProgram(p);
		gl.deleteShader(v);
		gl.deleteShader(f);
		if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
			const log = gl.getProgramInfoLog(p);
			gl.deleteProgram(p);
			throw new Error('GpuSystemsField program link failed: ' + log);
		}
		return p;
	}
}
